import {
    Checkbox,
    Dropdown,
    Icon,
    Icons,
    Info,
    Label,
    RadioGroup,
    TextInput,
    ThemesNames,
    Toggle,
    Tooltip,
    Validation
} from '@silverfort/sf-components';
import {
    DEFAULT_VENDOR_TO_ID,
    FEATURE_FLAGS_NAMES,
    JIT_MFA_TYPES,
    MFA_TYPES,
    POLICY_MODE_TYPES,
    PRIVILEGED_ACCOUNTS_STATUS
} from '@silverfort-nodelibs/common/utils/consts';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';
import React, { useCallback, useEffect, useMemo } from 'react';
import { getAllowedTokensOptions } from '../../../components/Policy/PoliciesUtils';
import useFeature from '../../../components/SystemSettings/hooks/featureFlags/useFeature';
import ValidationStatus from '../../../components/sf-components/Validation/ValidationStatus';
import { usePrivilegedAccountContext } from '../context/PrivilegedAccountsContext';
import { DEFAULT_JIT, JIT_DEFAULT_MAX_ENABLE_HOURS, JIT_DEFAULT_MIN_ENABLE_HOURS } from '../consts';

const JitPolicy = ({
    account,
    editedPolicy = DEFAULT_JIT,
    setEditedPolicy,
    isBreakGlassDisabled,
    isEditor,
    isActiveOnly = false
}) => {
    const jitDisabled = useMemo(() => {
        return (
            account?.status === PRIVILEGED_ACCOUNTS_STATUS.Disabling ||
            account?.status === PRIVILEGED_ACCOUNTS_STATUS.Enabling ||
            isBreakGlassDisabled ||
            !isEditor
        );
    }, [account?.status, isBreakGlassDisabled, isEditor]);

    const [tokesOptions, setTokesOptions] = React.useState([]);
    const [tokensDisplay, setTokensDisplay] = React.useState(new Set(editedPolicy.tokens));
    const [enterpriseOptions, setEnterpriseOptions] = React.useState([]);
    const [idps, setIdps] = React.useState([]);
    const [jitEnableTimeErrorMessage, setJitEnableTimeErrorMessage] = React.useState('');
    const systemMode = useFeature(FEATURE_FLAGS_NAMES.SystemMode);
    const isSaas = useMemo(() => systemMode !== SYSTEM_MODES.ON_PREM, [systemMode]);
    const { jitConfig } = usePrivilegedAccountContext();

    useEffect(() => {
        isActiveOnly && setEditedPolicy({ ...editedPolicy, protected: true });
    }, [isActiveOnly]);

    const calculateTokensOptions = useCallback(() => {
        if (jitConfig?.configuredTokens || jitConfig?.externalMfaTokens) {
            const tokens = getAllowedTokensOptions(
                [...tokensDisplay],
                jitConfig?.configuredTokens ?? [],
                false,
                true,
                POLICY_MODE_TYPES.ONLINE.value,
                false,
                isSaas,
                jitConfig?.externalMfaTokens ?? []
            )
                .filter((x) => x.value !== MFA_TYPES.BRIDGE_SSO.ID && x.value !== MFA_TYPES.RADIUS_OTP.ID)
                .map((option) => {
                    return {
                        value: option.value,
                        title: option.label,
                        disabled: option.disabled,
                        onClick: (value) => {
                            if (tokensDisplay.has(value)) {
                                tokensDisplay.delete(value);
                            } else {
                                tokensDisplay.add(value);
                            }
                            setTokensDisplay(tokensDisplay);
                            calculateTokensOptions();
                        }
                    };
                });
            setTokesOptions(tokens);
        }
    }, [tokensDisplay, jitConfig, isSaas]);

    useEffect(() => {
        if (!jitConfig) {
            return;
        }
        const newIdps = Object.values(jitConfig.ssoVendors)
            .filter((vendor) => Number(vendor.id) !== DEFAULT_VENDOR_TO_ID.DEFAULT)
            ?.map((v) => {
                return {
                    title: v.name,
                    value: v.id
                };
            });
        setIdps(newIdps);
    }, [jitConfig]);

    useEffect(() => {
        calculateTokensOptions();
    }, [calculateTokensOptions]);

    useEffect(() => {
        const options = Object.values(jitConfig?.ssoApplicationsSSOApplications ?? {})
            .filter((app) => Number(app.vendorId) === Number(editedPolicy.vendorId))
            .map((app) => ({
                value: app.id,
                title: app.applicationName
            }));
        setEnterpriseOptions(options ?? []);
    }, [editedPolicy.vendorId, jitConfig]);

    const enableTimeTextInput = useMemo(() => {
        const maxEnableHours = jitConfig?.maxEnableHours || JIT_DEFAULT_MAX_ENABLE_HOURS;
        return (
            <TextInput
                disabled={jitDisabled}
                className="max-w-30 h-8"
                value={editedPolicy.disableAfter}
                type="number"
                onKeyDown={(e) => {
                    if (e.key === '.') {
                        e.preventDefault();
                    }
                }}
                onChange={(value) => {
                    setJitEnableTimeErrorMessage(
                        value < JIT_DEFAULT_MIN_ENABLE_HOURS || value > maxEnableHours
                            ? `Select a value between ${JIT_DEFAULT_MIN_ENABLE_HOURS}-${maxEnableHours}`
                            : ''
                    );
                    setEditedPolicy((editedPolicy) => ({ ...editedPolicy, disableAfter: parseInt(value) }));
                }}
                max={maxEnableHours}
                min={JIT_DEFAULT_MIN_ENABLE_HOURS}
                defaultValue={JIT_DEFAULT_MIN_ENABLE_HOURS}
                validationMessage={jitEnableTimeErrorMessage}
            />
        );
    }, [jitDisabled, editedPolicy.disableAfter, jitConfig]);

    return (
        <div className="text-sf-gray-800">
            <div className="border flex rounded-lg items-center justify-between border-sf-gray-100 h-28">
                <div className="flex justify-between items-center gap-4">
                    <Icon
                        size={24}
                        className="border border-sf-gray-100 rounded-lg my-4 ml-4 p-2 text-sf-gray-400 col-span-1"
                        icon={Icons.Clock}></Icon>

                    <div className="flex flex-col items-between justify-center gap-1">
                        <Label className="w-40 text-16" text="Activate JIT policy" />
                        <div className="flex items-center text-sf-gray-500 text-14 gap-1 font-sf-primary">
                            Action will perform changes in AD once saved
                            <div>
                                <Tooltip
                                    className="w-full flex"
                                    title="By activating and saving a policy, selected account(s) will be disabled in Active Directory. To re-enable selected accounts, deactivate and save the policy.
Accounts disabled by the policy will be enabled once a user provides a reason and performs MFA.
If there is a deny policy, the user will not have access.">
                                    <Info />
                                </Tooltip>
                            </div>
                        </div>
                        <div className="sf-gray-400 text-12 max-w-[400px] font-sf-primary">
                            In order to restore disabled accounts in a break glass scenario, define an AD attribute in
                            the advanced configuration of the Admin Console.
                        </div>
                    </div>
                </div>

                {!isActiveOnly && (
                    <Toggle
                        disabled={jitDisabled}
                        large="true"
                        className="justify-self-end mr-4 col-span-1"
                        checked={editedPolicy.protected}
                        onChange={() => setEditedPolicy({ ...editedPolicy, protected: !editedPolicy.protected })}
                    />
                )}
            </div>
            <div className="mx-2 border-b border-sf-gray-100 flex flex-col py-4 gap-4 relative">
                <div className="content-center items-center flex py-4 h-8">
                    <Label
                        tooltipInfo="Account(s) will be disabled after the selected timeframe."
                        className="w-36"
                        theme={ThemesNames.Light}
                        text="Disable after"></Label>
                    <div className="flex flex-col absolute left-[144px] top-4">{enableTimeTextInput}</div>
                    <span className="ml-2 absolute left-[264px]">hours</span>
                </div>
                <div className="content-center items-center flex h-8">
                    <Label
                        className="w-36 text-nowrap"
                        text={'Require reason'}
                        tooltipInfo="If selected, the user who tries to use the account will be asked for a reason before the account is enabled. The reason will be further presented in system events, investigation page, log page and sent to SIEM (if syslog is configured). It is strongly recommended to require a reason for the end users before auto-enabling privileged accounts in the AD"></Label>
                    <Checkbox
                        disabled={jitDisabled}
                        checked={editedPolicy.reasonRequired}
                        onChange={() =>
                            setEditedPolicy({ ...editedPolicy, reasonRequired: !editedPolicy.reasonRequired })
                        }></Checkbox>
                    <Label
                        className={`pl-2 ${jitDisabled ? 'text-sf-gray-400' : ''}`}
                        text="Require justification prior to enabling the account"></Label>
                </div>
                <div className="content-center items-center flex h-8">
                    <Label className="w-36 text-nowrap" text={'Approval workflow'} />
                    <Checkbox
                        disabled={jitDisabled || !jitConfig?.isITSMDefined}
                        checked={jitConfig?.isITSMDefined ? editedPolicy.itsmProtected : false}
                        onChange={() =>
                            setEditedPolicy({ ...editedPolicy, itsmProtected: !editedPolicy.itsmProtected })
                        }></Checkbox>
                    <Label
                        className={`pl-2 ${jitDisabled || !jitConfig?.isITSMDefined ? 'text-sf-gray-400' : ''}`}
                        text=" Route access requests through your ITSM for pre-approval"></Label>
                </div>
            </div>
            <div>
                <div className="mx-2 h-16 content-center items-center flex">
                    <Label
                        className="w-36"
                        text="MFA"
                        tooltipInfo="It is strongly recommended to require MFA for the end users before auto-enabling privileged accounts in the AD"></Label>
                    <RadioGroup
                        groupClassName="font-sf-primary"
                        disabled={jitDisabled}
                        options={Object.values(JIT_MFA_TYPES)}
                        onChange={(value) => {
                            setEditedPolicy({
                                ...editedPolicy,
                                vendorId: null,
                                appId: null,
                                tokens: [],
                                mfaType: parseInt(value)
                            });
                        }}
                        selectedOption={editedPolicy.mfaType}></RadioGroup>
                </div>

                {editedPolicy.mfaType === JIT_MFA_TYPES.MFA.value && (
                    <div className="mx-2 border-b border-sf-gray-100 h-16 content-center items-center flex">
                        <Label className="w-36" theme={ThemesNames.Light} text="Tokens"></Label>
                        <span>
                            <Dropdown
                                disableSelectAll="true"
                                disabled={jitDisabled}
                                text="Tokens"
                                selectedItems={editedPolicy.tokens}
                                onChange={(values) => {
                                    setTokensDisplay(new Set(values));
                                    setEditedPolicy({ ...editedPolicy, tokens: values });
                                }}
                                size="small"
                                variant="trigger"
                                multiple="true"
                                items={tokesOptions}></Dropdown>
                            {!editedPolicy.tokens.length > 0 && (
                                <Validation
                                    validationStatus={ValidationStatus.Error}
                                    text="Required"
                                    showIcon={false}></Validation>
                            )}
                        </span>
                    </div>
                )}
                {editedPolicy.mfaType === JIT_MFA_TYPES.BRIDGE.value && (
                    <div className="mx-2 border-b border-sf-gray-100 ">
                        <div className="mx-2 h-16 content-center items-center flex">
                            <Label className="w-36" theme={ThemesNames.Light} text="IdP"></Label>
                            <span>
                                <Dropdown
                                    disabled={jitDisabled}
                                    text="Idp"
                                    selectedItems={editedPolicy.vendorId}
                                    onChange={(value) =>
                                        setEditedPolicy({ ...editedPolicy, vendorId: value, appId: null })
                                    }
                                    size="small"
                                    variant="trigger"
                                    items={idps}></Dropdown>
                                {!editedPolicy.vendorId && (
                                    <Validation
                                        validationStatus={ValidationStatus.Error}
                                        text="Required"
                                        showIcon={false}></Validation>
                                )}
                            </span>
                        </div>

                        <div className="mx-2  h-16 content-center items-center flex">
                            <Label className="w-36" theme={ThemesNames.Light} text="Enterprise app"></Label>
                            <span>
                                <Dropdown
                                    disabled={jitDisabled}
                                    text="Enterprise app"
                                    selectedItems={editedPolicy.appId}
                                    onChange={(value) => setEditedPolicy({ ...editedPolicy, appId: value })}
                                    size="small"
                                    variant="trigger"
                                    items={enterpriseOptions}></Dropdown>
                                {!editedPolicy.appId && (
                                    <Validation
                                        validationStatus={ValidationStatus.Error}
                                        text="Required"
                                        showIcon={false}></Validation>
                                )}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
export default JitPolicy;
